/** XeAgents.ai - AI Flow Creation Page (Full Page) */
import { useState, useRef } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Sparkles,
  Loader2,
  Upload,
  FileText,
  X,
  CheckCircle,
  Lightbulb,
  ClipboardList,
  ChevronRight,
  ChevronLeft,
  Edit3,
  Plus,
  Trash2,
  Calendar,
  Phone,
  ArrowRight,
  ArrowLeft,
  RefreshCw,
  Zap,
  HelpCircle,
  Rocket,
  Workflow,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { AuthStorage } from "@/lib/auth-storage";

// FIX #8: Get CSRF token from cookie with improved URL decoding
function getCsrfToken(): string | null {
  try {
    const cookies = document.cookie.split(";");
    for (const cookie of cookies) {
      const trimmed = cookie.trim();
      const eqIndex = trimmed.indexOf("=");
      if (eqIndex === -1) continue;

      const name = trimmed.substring(0, eqIndex);
      const value = trimmed.substring(eqIndex + 1);

      if (name === "XSRF-TOKEN") {
        // FIX #8: Handle multiple encoding edge cases
        try {
          // First try standard decoding
          return decodeURIComponent(value);
        } catch {
          // If decoding fails, try to handle double-encoded values
          try {
            return decodeURIComponent(decodeURIComponent(value));
          } catch {
            // If all decoding fails, return the raw value
            return value;
          }
        }
      }
    }
  } catch (e) {
    console.error("Error parsing CSRF token:", e);
  }
  return null;
}

// Types matching backend ExtractedRequirements
interface FieldToCollect {
  id: string;
  name: string;
  type: 'text' | 'email' | 'phone' | 'number' | 'date' | 'yes_no' | 'multiple_choice';
  question: string;
  required: boolean;
  options?: string[];
  variableName: string;
}

interface BranchingLogic {
  id: string;
  conditionQuestion: string;
  condition: string;
  yesAction: 'continue' | 'book_appointment' | 'transfer' | 'end';
  yesActionDetail?: string;
  noAction: 'continue' | 'end' | 'callback';
  noActionDetail?: string;
}

// Flow template types
type FlowTemplateType =
  | 'sales_pitch'
  | 'exhibitor_acquisition'
  | 'hr_screening'
  | 'customer_support'
  | 'appointment_booking'
  | 'lead_qualification'
  | 'survey_feedback'
  | 'order_taking'
  | 'debt_collection'
  | 'real_estate'
  | 'insurance_quote'
  | 'custom';

interface ExtractedRequirements {
  agentName: string;
  agentPurpose: string;
  targetAudience: string;
  callType: 'outbound' | 'inbound' | 'both';
  flowTemplateType: FlowTemplateType;
  greeting: string;
  introduction: string;
  greetingVariants?: Array<{
    id: string;
    name: string;
    message: string;
    useWhen?: string;
  }>;
  valuePitch?: {
    mainPitch: string;
    bulletPoints: string[];
    trialOffer?: string;
    noObligationStatement?: string;
  };
  fieldsToCollect: FieldToCollect[];
  actions: {
    bookAppointment: boolean;
    appointmentType?: string;
    transferToAgent: boolean;
    transferReason?: string;
    sendFollowUp: boolean;
    followUpType?: string;
  };
  branchingLogic: BranchingLogic[];
  objectionHandlers?: Array<{
    id: string;
    objection: string;
    objectionExamples: string[];
    response: string;
    action: 'continue' | 'callback' | 'end' | 'book_appointment';
    collectReferral?: boolean;
  }>;
  qualificationRules?: Array<{
    id: string;
    field: string;
    operator: string;
    value: any;
    fallbackField?: string;
    qualifiedAction: string;
    unqualifiedAction: string;
  }>;
  closingMessage: string;
  dataReuseInfo: {
    hasNameField: boolean;
    hasEmailField: boolean;
    hasPhoneField: boolean;
    appointmentWillReuseData: boolean;
  };
  suggestions: string[];
  confidence: number;
}

const LANGUAGES = [
  { code: "en", name: "English" },
  { code: "hi", name: "Hindi" },
  { code: "es", name: "Spanish" },
  { code: "fr", name: "French" },
  { code: "de", name: "German" },
  { code: "pt", name: "Portuguese" },
  { code: "ta", name: "Tamil" },
  { code: "te", name: "Telugu" },
  { code: "mr", name: "Marathi" },
  { code: "bn", name: "Bengali" },
];

const FIELD_TYPES = [
  { value: "text", label: "Text" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "yes_no", label: "Yes/No" },
  { value: "multiple_choice", label: "Multiple Choice" },
];

// Flow template options with descriptions
const FLOW_TEMPLATES: Array<{
  value: FlowTemplateType;
  name: string;
  description: string;
  icon: string;
  structure: string[];
  usesStateMachine?: boolean; // Templates that generate multi-call state machine workflows
}> = [
  {
    value: 'sales_pitch',
    name: '⭐ Outbound Sales (State Machine)',
    description: 'RECOMMENDED: Consolidated flow following your playbook - discovery, pitch, objections & booking',
    icon: '🚀',
    structure: ['Opening', 'Discovery/Qualification', 'Value Pitch', 'Objection Handling', 'Booking/CTA', 'Outcomes'],
    usesStateMachine: true
  },
  {
    value: 'exhibitor_acquisition',
    name: '🎪 Exhibition/Trade Show',
    description: 'Exhibitor acquisition for trade shows & events - pitch event value, then qualify & book consultations',
    icon: '🎪',
    structure: ['Opening', 'Gatekeeper', 'Event Intro', 'Exhibitor Benefits', 'Discovery', 'Qualification', 'Booking'],
    usesStateMachine: true
  },
  {
    value: 'hr_screening',
    name: 'HR Screening',
    description: 'Screen deal-breakers EARLY, qualify candidates, schedule interviews',
    icon: '👥',
    structure: ['Verify Identity', 'Confirm Interest', 'Deal-Breakers', 'Basic Requirements', 'Skills', 'Salary', 'Schedule/Decline']
  },
  {
    value: 'customer_support',
    name: 'Customer Support',
    description: 'Inbound support calls with issue resolution and escalation',
    icon: '🎧',
    structure: ['Greeting', 'Issue Identification', 'Troubleshooting', 'Resolution/Transfer', 'Satisfaction Check']
  },
  {
    value: 'appointment_booking',
    name: 'Appointment Booking',
    description: 'Simple appointment scheduling flow',
    icon: '📅',
    structure: ['Greeting', 'Collect Info', 'Service Selection', 'Date/Time', 'Confirmation']
  },
  {
    value: 'lead_qualification',
    name: 'Lead Qualification',
    description: 'Multi-call qualification with smart follow-ups based on BANT criteria',
    icon: '🎯',
    structure: ['Initial Contact', 'Qualify Budget', 'Qualify Authority', 'Qualify Need', 'Follow-up', 'Route'],
    usesStateMachine: true
  },
  {
    value: 'survey_feedback',
    name: 'Survey & Feedback',
    description: 'Collect customer feedback and satisfaction ratings',
    icon: '📊',
    structure: ['Greeting', 'Purpose Explanation', 'Survey Questions', 'Comments', 'Thank You']
  },
  {
    value: 'order_taking',
    name: 'Order Taking',
    description: 'Take orders with customization and delivery details',
    icon: '🛒',
    structure: ['Greeting', 'Order Items', 'Customizations', 'Delivery Details', 'Payment', 'Confirmation']
  },
  {
    value: 'debt_collection',
    name: 'Debt Collection',
    description: 'Multi-call collection workflow with payment arrangement follow-ups',
    icon: '💳',
    structure: ['Initial Contact', 'Verify Identity', 'Payment Options', 'Arrangement', 'Follow-up', 'Confirmation'],
    usesStateMachine: true
  },
  {
    value: 'real_estate',
    name: 'Real Estate',
    description: 'Property inquiry and viewing scheduling',
    icon: '🏠',
    structure: ['Greeting', 'Property Interest', 'Requirements', 'Pre-qualification', 'Schedule Viewing']
  },
  {
    value: 'insurance_quote',
    name: 'Insurance Quote',
    description: 'Insurance inquiry and quote generation',
    icon: '🛡️',
    structure: ['Greeting', 'Insurance Type', 'Coverage Requirements', 'Personal Details', 'Quote', 'Follow-up']
  },
  {
    value: 'custom',
    name: 'Custom Flow',
    description: 'Neutral flow - follows your playbook exactly without template assumptions',
    icon: '⚙️',
    structure: ['Follows playbook structure exactly']
  }
];

type Step = 'template' | 'upload' | 'review' | 'generate';

export default function AIFlowCreatePage() {
  const [, setLocation] = useLocation();
  const [step, setStep] = useState<Step>('template');
  const [selectedTemplate, setSelectedTemplate] = useState<FlowTemplateType | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [language, setLanguage] = useState("en");
  const [additionalContext, setAdditionalContext] = useState("");
  const [flowName, setFlowName] = useState("");
  const [requirements, setRequirements] = useState<ExtractedRequirements | null>(null);
  const [documentMetadata, setDocumentMetadata] = useState<any>(null);
  const [generatedFlow, setGeneratedFlow] = useState<any>(null);
  const [createdForm, setCreatedForm] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  // State machine result (when template creates a multi-state workflow)
  const [stateMachineResult, setStateMachineResult] = useState<{
    stateMachine: { id: string; name: string };
    flows: Array<{ stateId: string; stateName: string; flowId: string; flowName: string }>;
    blueprint: { name: string; description?: string; industry?: string; statesCount: number; transitionsCount: number; persistedVariables: string[] };
  } | null>(null);
  const [campaignName, setCampaignName] = useState("");
  const [selectedAgentId, setSelectedAgentId] = useState<string>("");

  // Fetch agents for campaign creation (only when state machine result exists)
  const { data: agents = [] } = useQuery<Array<{ id: string; name: string; type: string }>>({
    queryKey: ["/api/agents"],
    enabled: !!stateMachineResult,
  });

  // Build headers helper
  const buildHeaders = (): Record<string, string> => {
    const headers: Record<string, string> = {};
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      headers['X-XSRF-TOKEN'] = csrfToken;
    }
    const authHeader = AuthStorage.getAuthHeader();
    if (authHeader) {
      headers['Authorization'] = authHeader;
    }
    return headers;
  };

  // Step 1: Extract requirements from document
  const extractMutation = useMutation({
    mutationFn: async (data: { file: File; additionalContext?: string; templateType?: FlowTemplateType }) => {
      const formData = new FormData();
      formData.append('document', data.file);
      if (data.additionalContext) {
        formData.append('additionalContext', data.additionalContext);
      }
      if (data.templateType) {
        formData.append('templateType', data.templateType);
      }

      const res = await fetch('/api/flow-automation/flows/extract-requirements', {
        method: 'POST',
        body: formData,
        credentials: 'include',
        headers: buildHeaders(),
      });

      if (!res.ok) {
        // Check content type to handle HTML error pages (e.g., auth redirects)
        const contentType = res.headers.get('content-type') || '';
        if (!contentType.includes('application/json')) {
          // Server returned HTML instead of JSON - likely an auth issue or server error
          if (res.status === 401 || res.status === 403) {
            throw new Error('Session expired. Please refresh the page and try again.');
          }
          throw new Error(`Server error (${res.status}). Please try again or contact support.`);
        }
        const errorData = await res.json();
        throw new Error(errorData.message || errorData.error || 'Failed to extract requirements');
      }

      // Also check content type for successful responses
      const contentType = res.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error('Invalid server response. Please try again.');
      }

      return res.json();
    },
    onSuccess: (data) => {
      // FIX #6: Check for template type mismatch and warn user
      const detectedTemplate = data.requirements.flowTemplateType;
      let templateMismatchWarning = "";

      if (selectedTemplate === "custom") {
        // User explicitly selected Custom Flow - keep it neutral and playbook-driven
        // Do NOT let auto-detection override to exhibitor_acquisition or other templates
        data.requirements.flowTemplateType = "custom";
        if (detectedTemplate && detectedTemplate !== "custom") {
          const detectedName = FLOW_TEMPLATES.find(t => t.value === detectedTemplate)?.name || detectedTemplate;
          templateMismatchWarning = `AI detected "${detectedName}" patterns, but using Custom Flow as selected. Flow will be generated based on your playbook content.`;
        }
      } else if (selectedTemplate && detectedTemplate !== selectedTemplate) {
        const selectedName = FLOW_TEMPLATES.find(t => t.value === selectedTemplate)?.name || selectedTemplate;
        const detectedName = FLOW_TEMPLATES.find(t => t.value === detectedTemplate)?.name || detectedTemplate;
        templateMismatchWarning = `Note: Your document appears to be "${detectedName}" but you selected "${selectedName}". Using your selection.`;

        // Override with user's selection
        data.requirements.flowTemplateType = selectedTemplate;
      } else if (selectedTemplate) {
        data.requirements.flowTemplateType = selectedTemplate;
      }

      setRequirements(data.requirements);
      setDocumentMetadata(data.documentMetadata);
      setFlowName(data.requirements.agentName || "");
      setStep("review");

      const templateName = FLOW_TEMPLATES.find(t => t.value === data.requirements.flowTemplateType)?.name || "Custom";

      // FIX #3: Show truncation warning if document was truncated
      if (data.truncationWarning) {
        toast({
          title: "Document Truncated",
          description: data.truncationWarning,
          variant: "destructive",
          duration: 10000, // Show for longer since it's important
        });
      }

      // FIX #6: Show template mismatch warning
      if (templateMismatchWarning) {
        toast({
          title: "Template Mismatch",
          description: templateMismatchWarning,
          duration: 8000,
        });
      }

      toast({
        title: "Requirements Extracted!",
        description: `Found ${data.requirements.fieldsToCollect.length} fields • Template: ${templateName}`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Extraction Failed",
        description: error.message || "Failed to extract requirements from document",
        variant: "destructive",
      });
    },
  });

  // Step 3: Generate flow from requirements
  const generateMutation = useMutation({
    mutationFn: async (data: { requirements: ExtractedRequirements; language: string; flowName?: string }) => {
      // State machine templates (sales_pitch, lead_qualification, debt_collection) will automatically
      // generate multi-flow state machines on the backend based on flowTemplateType
      const res = await fetch('/api/flow-automation/flows/generate-from-requirements', {
        method: 'POST',
        body: JSON.stringify(data),
        credentials: 'include',
        headers: {
          ...buildHeaders(),
          'Content-Type': 'application/json',
        },
      });

      if (!res.ok) {
        // Check content type to handle HTML error pages (e.g., auth redirects)
        const contentType = res.headers.get('content-type') || '';
        if (!contentType.includes('application/json')) {
          // Server returned HTML instead of JSON - likely an auth issue or server error
          if (res.status === 401 || res.status === 403) {
            throw new Error('Session expired. Please refresh the page and try again.');
          }
          throw new Error(`Server error (${res.status}). Please try again or contact support.`);
        }
        const errorData = await res.json();
        throw new Error(errorData.message || errorData.error || 'Failed to generate flow');
      }

      // Also check content type for successful responses
      const contentType = res.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error('Invalid server response. Please try again.');
      }

      return res.json();
    },
    onSuccess: (data) => {
      // Check if this is a state machine result (multi-call workflow)
      if (data.mode === 'state_machine' && data.stateMachine) {
        setStateMachineResult({
          stateMachine: data.stateMachine,
          flows: data.flows || [],
          blueprint: data.blueprint || {},
        });
        setCampaignName(data.stateMachine.name || 'New Campaign');
        setGeneratedFlow(null); // Clear single flow state
        setStep("generate");
        toast({
          title: "Smart Follow-Up Workflow Created!",
          description: `Created "${data.stateMachine.name}" with ${data.flows?.length || 0} flows for multi-call orchestration.`,
        });
        return;
      }

      // Single flow result
      setStateMachineResult(null);
      setGeneratedFlow(data.flow);
      setCreatedForm(data.form);
      setStep("generate");

      // FIX #4: Show validation warnings from backend (disconnected intents, missing branches, etc.)
      if (data.validationWarnings && data.validationWarnings.length > 0) {
        // Show first 3 warnings to avoid overwhelming the user
        const warningsToShow = data.validationWarnings.slice(0, 3);
        warningsToShow.forEach((warning: string, index: number) => {
          setTimeout(() => {
            toast({
              title: "Flow Needs Attention",
              description: warning,
              duration: 8000,
            });
          }, index * 1000); // Stagger toasts
        });

        if (data.validationWarnings.length > 3) {
          setTimeout(() => {
            toast({
              title: "Additional Warnings",
              description: `${data.validationWarnings.length - 3} more issue(s) detected. Please review the flow in the editor.`,
              duration: 6000,
            });
          }, 3500);
        }
      }

      toast({
        title: "Flow Generated!",
        description: `Created flow with ${data.flow?.nodes?.length || 0} nodes`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Generation Failed",
        description: error.message || "Failed to generate flow",
        variant: "destructive",
      });
    },
  });

  // Update AI flow mutation (flow is already created during generation)
  const updateAIFlowMutation = useMutation({
    mutationFn: async ({ flowId, flowData }: { flowId: string; flowData: any }) => {
      const res = await apiRequest("PATCH", `/api/flow-automation/flows/${flowId}`, flowData);
      return res.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/flow-automation/flows"] });
      queryClient.invalidateQueries({ queryKey: ["/api/flow-automation/forms"] });
      toast({
        title: "Flow Saved!",
        description: "Your AI-generated flow has been saved",
      });
      setLocation(`/app/flows/${data.id}`);
    },
    onError: (error: any) => {
      toast({
        title: "Error saving flow",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Campaign creation mutation for state machine workflows
  const createCampaignMutation = useMutation({
    mutationFn: async (data: { name: string; stateMachineId: string; agentId: string }) => {
      const res = await apiRequest("POST", "/api/campaigns", {
        name: data.name,
        type: "outbound",
        stateMachineEnabled: true,
        stateMachineId: data.stateMachineId,
        agentId: data.agentId,
        status: "pending",
      });
      return res.json();
    },
    onSuccess: (campaign) => {
      queryClient.invalidateQueries({ queryKey: ["/api/campaigns"] });
      toast({
        title: "Campaign Created!",
        description: `Smart Follow-Up campaign "${campaign.name}" is ready. Add contacts to start.`,
      });
      setLocation(`/campaigns/${campaign.id}`);
    },
    onError: (error: any) => {
      toast({
        title: "Campaign Creation Failed",
        description: error.message || "Failed to create campaign",
        variant: "destructive",
      });
    },
  });

  const handleCreateCampaign = () => {
    if (!selectedAgentId) {
      toast({
        title: "Agent Required",
        description: "Please select an AI agent for this campaign",
        variant: "destructive",
      });
      return;
    }
    if (stateMachineResult) {
      createCampaignMutation.mutate({
        name: campaignName || stateMachineResult.stateMachine.name,
        stateMachineId: stateMachineResult.stateMachine.id,
        agentId: selectedAgentId,
      });
    }
  };

  // Handle file selection
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain', 'text/markdown'];
      const allowedExts = ['.pdf', '.docx', '.txt', '.md'];
      const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));

      if (!allowedTypes.includes(file.type) && !allowedExts.includes(ext)) {
        toast({
          title: "Invalid File Type",
          description: "Please upload a PDF, DOCX, TXT, or Markdown file",
          variant: "destructive",
        });
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: "File Too Large",
          description: "Document must be under 10MB",
          variant: "destructive",
        });
        return;
      }

      setUploadedFile(file);
      setRequirements(null);
      setGeneratedFlow(null);
      setStateMachineResult(null);
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleExtract = () => {
    if (!uploadedFile) {
      toast({
        title: "No Document",
        description: "Please upload a use case document",
        variant: "destructive",
      });
      return;
    }

    extractMutation.mutate({
      file: uploadedFile,
      additionalContext: additionalContext || undefined,
      templateType: selectedTemplate || undefined,
    });
  };

  const handleGenerate = () => {
    if (!requirements) return;
    // Prevent double-clicks - return if mutation is already in progress
    if (generateMutation.isPending) return;

    generateMutation.mutate({
      requirements,
      language,
      flowName: flowName || undefined,
    });
  };

  const handleSave = () => {
    if (generatedFlow && generatedFlow.id) {
      // Flow was already created during generation, just update the name if changed
      updateAIFlowMutation.mutate({
        flowId: generatedFlow.id,
        flowData: {
          name: flowName || generatedFlow.name,
        },
      });
    }
  };

  const handleCancel = () => {
    setLocation('/app/flows');
  };

  // Field editing helpers
  const updateField = (fieldId: string, updates: Partial<FieldToCollect>) => {
    if (!requirements) return;
    setRequirements({
      ...requirements,
      fieldsToCollect: requirements.fieldsToCollect.map(f =>
        f.id === fieldId ? { ...f, ...updates } : f
      )
    });
  };

  const removeField = (fieldId: string) => {
    if (!requirements) return;
    setRequirements({
      ...requirements,
      fieldsToCollect: requirements.fieldsToCollect.filter(f => f.id !== fieldId)
    });
  };

  const addField = () => {
    if (!requirements) return;
    const newField: FieldToCollect = {
      id: `field_${Date.now()}`,
      name: 'New Field',
      type: 'text',
      question: 'Enter your question here',
      required: true,
      variableName: `new_field_${requirements.fieldsToCollect.length + 1}`
    };
    setRequirements({
      ...requirements,
      fieldsToCollect: [...requirements.fieldsToCollect, newField]
    });
  };

  // Render step indicator
  const renderStepIndicator = () => (
    <div className="flex items-center justify-center gap-2 mb-6 flex-wrap">
      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
        step === 'template' ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/50 dark:text-teal-300' : 'bg-muted text-muted-foreground'
      }`}>
        <ClipboardList className="h-4 w-4" />
        Template
      </div>
      <ChevronRight className="h-4 w-4 text-muted-foreground" />
      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
        step === 'upload' ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/50 dark:text-teal-300' : 'bg-muted text-muted-foreground'
      }`}>
        <Upload className="h-4 w-4" />
        Upload
      </div>
      <ChevronRight className="h-4 w-4 text-muted-foreground" />
      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
        step === 'review' ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/50 dark:text-teal-300' : 'bg-muted text-muted-foreground'
      }`}>
        <Edit3 className="h-4 w-4" />
        Review
      </div>
      <ChevronRight className="h-4 w-4 text-muted-foreground" />
      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
        step === 'generate' ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/50 dark:text-teal-300' : 'bg-muted text-muted-foreground'
      }`}>
        <Sparkles className="h-4 w-4" />
        Generate
      </div>
    </div>
  );

  // Document requirements guide for each template
  const getDocumentGuide = (template: FlowTemplateType): { title: string; sections: { name: string; items: string[] }[] } => {
    const guides: Record<FlowTemplateType, { title: string; sections: { name: string; items: string[] }[] }> = {
      sales_pitch: {
        title: 'Sales Pitch Document Should Include',
        sections: [
          { name: 'Value Proposition / Pitch', items: ['Main pitch paragraph', 'Key benefits (3-5 bullet points)', 'Trial offer or guarantee'] },
          { name: 'Objection Handling', items: ['"Not interested" response', '"Already have one" response', '"Can you email it?" response'] },
          { name: 'Qualification Criteria', items: ['Minimum staff/company size', 'Decision maker requirement', 'Budget indicators'] },
          { name: 'Data to Capture', items: ['Business name, contact, phone', 'Staff count / company size', 'Preferred meeting time'] }
        ]
      },
      exhibitor_acquisition: {
        title: 'Exhibition/Trade Show Document Should Include',
        sections: [
          { name: 'Event Details', items: ['Event name and dates', 'Location', 'Expected attendees/exhibitors'] },
          { name: 'Exhibitor Benefits', items: ['Lead generation stats', 'Buyer access', 'Brand visibility', 'ROI metrics'] },
          { name: 'Qualification Criteria', items: ['Industry fit', 'Budget range', 'Decision timeline'] },
          { name: 'Data to Capture', items: ['Company name, contact', 'Products/services offered', 'Target buyers', 'Booth size interest'] }
        ]
      },
      hr_screening: {
        title: 'HR Screening Document Should Include',
        sections: [
          { name: 'Deal-Breakers (Screen EARLY)', items: ['Location requirement', 'Work authorization', 'Availability to start'] },
          { name: 'Requirements', items: ['Years of experience', 'Required certifications', 'Technical skills'] },
          { name: 'Salary & Scheduling', items: ['Salary range', 'Interview availability', 'Next steps timeline'] }
        ]
      },
      customer_support: {
        title: 'Customer Support Document Should Include',
        sections: [
          { name: 'Common Issues', items: ['Issue categories', 'Troubleshooting steps'] },
          { name: 'Escalation', items: ['When to transfer', 'Urgent indicators'] }
        ]
      },
      appointment_booking: {
        title: 'Appointment Booking Document Should Include',
        sections: [
          { name: 'Services', items: ['List of services', 'Duration per service'] },
          { name: 'Scheduling', items: ['Available days/hours', 'Lead time requirements'] }
        ]
      },
      lead_qualification: {
        title: 'Lead Qualification Document Should Include',
        sections: [
          { name: 'BANT Criteria', items: ['Budget threshold', 'Authority questions', 'Timeline requirements'] },
          { name: 'Routing', items: ['Qualified lead actions', 'Unqualified lead handling'] }
        ]
      },
      survey_feedback: {
        title: 'Survey Document Should Include',
        sections: [
          { name: 'Questions', items: ['Rating questions', 'Open-ended questions'] }
        ]
      },
      order_taking: {
        title: 'Order Taking Document Should Include',
        sections: [
          { name: 'Menu/Products', items: ['Items available', 'Customizations'] },
          { name: 'Delivery', items: ['Delivery areas', 'Payment options'] }
        ]
      },
      debt_collection: {
        title: 'Debt Collection Document Should Include',
        sections: [
          { name: 'Verification', items: ['Identity verification', 'Account confirmation'] },
          { name: 'Payment', items: ['Payment options', 'Arrangement terms'] }
        ]
      },
      real_estate: {
        title: 'Real Estate Document Should Include',
        sections: [
          { name: 'Property Interest', items: ['Buying/selling/renting', 'Property types'] },
          { name: 'Requirements', items: ['Location, budget, size'] }
        ]
      },
      insurance_quote: {
        title: 'Insurance Quote Document Should Include',
        sections: [
          { name: 'Insurance Types', items: ['Coverage types', 'Quote information needed'] }
        ]
      },
      custom: {
        title: 'Custom Flow Document Should Include',
        sections: [
          { name: 'Agent Purpose', items: ['What is this agent for?', 'Target audience'] },
          { name: 'Flow', items: ['Questions to ask', 'Actions to take'] }
        ]
      }
    };
    return guides[template] || guides.custom;
  };

  // Render Template Step
  const renderTemplateStep = () => (
    <div className="space-y-6">
      <Alert className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 border-amber-200 dark:border-amber-800">
        <ClipboardList className="h-4 w-4 text-amber-600" />
        <AlertDescription className="text-amber-800 dark:text-amber-200">
          <strong className="flex items-center gap-1">
            <span className="text-red-500">*</span> Template Selection Required
          </strong>
          <span className="block mt-1">
            Choose a template that matches your use case. This determines the flow structure and ensures
            high-conversion patterns (e.g., Sales Pitch: Greet → Hook → Qualify → Pitch → Close).
          </span>
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {FLOW_TEMPLATES.map((template) => (
          <Card
            key={template.value}
            className={`cursor-pointer transition-all hover:shadow-md ${
              selectedTemplate === template.value
                ? 'ring-2 ring-teal-500 bg-teal-50 dark:bg-teal-950/30'
                : 'hover:border-teal-300 dark:hover:border-teal-700'
            }`}
            onClick={() => setSelectedTemplate(template.value)}
          >
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <span className="text-3xl">{template.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm flex items-center gap-1 flex-wrap">
                    {template.name}
                    {template.usesStateMachine && (
                      <Badge variant="outline" className="text-[10px] py-0 px-1.5 bg-purple-50 dark:bg-purple-950/50 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700">
                        <RefreshCw className="h-2.5 w-2.5 mr-0.5" />
                        Multi-Call
                      </Badge>
                    )}
                    {selectedTemplate === template.value && (
                      <CheckCircle className="h-4 w-4 text-teal-600" />
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                    {template.description}
                  </p>
                </div>
              </div>
              {selectedTemplate === template.value && (
                <div className="mt-3 pt-3 border-t">
                  <p className="text-xs font-medium text-muted-foreground mb-2">
                    {template.usesStateMachine ? 'State Machine Workflow:' : 'Flow Structure:'}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {template.structure.map((step, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs py-0">
                        {step}
                      </Badge>
                    ))}
                  </div>
                  {template.usesStateMachine && (
                    <p className="text-xs text-purple-600 dark:text-purple-400 mt-2 flex items-center gap-1">
                      <RefreshCw className="h-3 w-3" />
                      Creates smart follow-up campaign with multiple call states
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {selectedTemplate && (
        <div className="space-y-4">
          {(() => {
            const selectedTemplateData = FLOW_TEMPLATES.find(t => t.value === selectedTemplate);
            const isStateMachine = selectedTemplateData?.usesStateMachine;
            return (
              <div className={`flex items-center justify-between p-4 rounded-lg border ${
                isStateMachine
                  ? 'bg-purple-50 dark:bg-purple-950/30 border-purple-200 dark:border-purple-800'
                  : 'bg-teal-50 dark:bg-teal-950/30 border-teal-200 dark:border-teal-800'
              }`}>
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{selectedTemplateData?.icon}</span>
                  <div>
                    <div className="font-medium flex items-center gap-2">
                      {selectedTemplateData?.name} selected
                      {isStateMachine && (
                        <Badge variant="outline" className="text-[10px] py-0 px-1.5 bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 border-purple-400">
                          <RefreshCw className="h-2.5 w-2.5 mr-0.5" />
                          Smart Follow-Up
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {isStateMachine
                        ? 'Creates multi-call campaign with automatic follow-ups'
                        : 'Flow will follow this structure automatically'}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => setStep('upload')}
                  className={isStateMachine ? "bg-purple-600 hover:bg-purple-700" : "bg-teal-600 hover:bg-teal-700"}
                >
                  Continue <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            );
          })()}

          {/* Document requirements guide */}
          <Card className="bg-blue-50/50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-blue-800 dark:text-blue-200 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                {getDocumentGuide(selectedTemplate).title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {getDocumentGuide(selectedTemplate).sections.map((section, idx) => (
                  <div key={idx} className="text-xs">
                    <p className="font-medium text-blue-700 dark:text-blue-300 mb-1">{section.name}</p>
                    <ul className="space-y-0.5 text-blue-600 dark:text-blue-400">
                      {section.items.map((item, i) => (
                        <li key={i} className="flex items-start gap-1">
                          <span className="text-blue-400">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {!selectedTemplate && (
        <div className="flex items-center justify-center p-8 bg-muted/50 rounded-lg border border-dashed border-muted-foreground/30">
          <div className="text-center">
            <ClipboardList className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
            <p className="text-muted-foreground">
              Select a template above to continue
            </p>
          </div>
        </div>
      )}
    </div>
  );

  // Render Upload Step
  const renderUploadStep = () => (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Show selected template */}
      {selectedTemplate && (
        <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
          <div className="flex items-center gap-2">
            <span className="text-xl">{FLOW_TEMPLATES.find(t => t.value === selectedTemplate)?.icon}</span>
            <span className="font-medium">
              {FLOW_TEMPLATES.find(t => t.value === selectedTemplate)?.name}
            </span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setStep('template')}>
            Change
          </Button>
        </div>
      )}

      <Alert className="bg-teal-50 dark:bg-teal-950/30 border-teal-200 dark:border-teal-800">
        <FileText className="h-4 w-4 text-teal-600" />
        <AlertDescription className="text-teal-800 dark:text-teal-200">
          Upload a document describing your AI agent's purpose, conversation flow, and data to collect.
          We'll extract the requirements for you to review before generating the flow.
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Language</Label>
          <Select value={language} onValueChange={setLanguage}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LANGUAGES.map((lang) => (
                <SelectItem key={lang.code} value={lang.code}>{lang.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* File Upload Area */}
      <div className="space-y-2">
        <Label>Use Case Document</Label>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.docx,.txt,.md"
          onChange={handleFileSelect}
          className="hidden"
        />

        {!uploadedFile ? (
          <div
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed rounded-lg p-12 text-center cursor-pointer hover:border-teal-400 hover:bg-teal-50/50 dark:hover:bg-teal-950/30 transition-colors"
          >
            <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="font-medium text-lg">Click to upload or drag and drop</p>
            <p className="text-sm text-muted-foreground mt-2">
              PDF, DOCX, TXT, or Markdown (max 10MB)
            </p>
          </div>
        ) : (
          <div className="border rounded-lg p-4 bg-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-teal-100 dark:bg-teal-900">
                  <FileText className="h-8 w-8 text-teal-600 dark:text-teal-300" />
                </div>
                <div>
                  <p className="font-medium">{uploadedFile.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {(uploadedFile.size / 1024).toFixed(1)} KB
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={handleRemoveFile}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Additional Context */}
      <div className="space-y-2">
        <Label>Additional Context (Optional)</Label>
        <Textarea
          value={additionalContext}
          onChange={(e) => setAdditionalContext(e.target.value)}
          placeholder="Add any specific requirements not in the document..."
          rows={3}
        />
      </div>

      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={() => setStep('template')}>
          <ChevronLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <Button
          onClick={handleExtract}
          disabled={extractMutation.isPending || !uploadedFile}
          className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700"
        >
          {extractMutation.isPending ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Analyzing Document...</>
          ) : (
            <><Zap className="h-4 w-4 mr-2" /> Extract Requirements</>
          )}
        </Button>
      </div>
    </div>
  );

  // Render Review Step
  const renderReviewStep = () => {
    if (!requirements) return null;

    return (
      <div className="max-w-3xl mx-auto space-y-4">
        {/* Confidence Score */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Extraction Confidence</span>
          <Badge variant={requirements.confidence >= 80 ? "default" : requirements.confidence >= 60 ? "secondary" : "destructive"}>
            {requirements.confidence}%
          </Badge>
        </div>

        {/* Agent Info */}
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-teal-500" />
              Agent Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-2">
              <Label className="text-xs">Agent Name</Label>
              <Input
                value={flowName || requirements.agentName}
                onChange={(e) => setFlowName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Purpose</Label>
              <Textarea
                value={requirements.agentPurpose}
                onChange={(e) => setRequirements({ ...requirements, agentPurpose: e.target.value })}
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs">Target Audience</Label>
                <Input
                  value={requirements.targetAudience}
                  onChange={(e) => setRequirements({ ...requirements, targetAudience: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Call Type</Label>
                <Select
                  value={requirements.callType}
                  onValueChange={(v) => setRequirements({ ...requirements, callType: v as any })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="outbound">Outbound</SelectItem>
                    <SelectItem value="inbound">Inbound</SelectItem>
                    <SelectItem value="both">Both</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Fields to Collect */}
        <Card>
          <CardHeader className="py-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <ClipboardList className="h-4 w-4 text-teal-500" />
                Data to Collect ({requirements.fieldsToCollect.length} fields)
              </CardTitle>
              <Button variant="outline" size="sm" onClick={addField}>
                <Plus className="h-3 w-3 mr-1" /> Add
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="max-h-[300px]">
              <div className="space-y-3 pr-4">
                {requirements.fieldsToCollect.map((field, index) => (
                  <div key={field.id} className="border rounded-lg p-3 bg-muted/30">
                    <div className="flex items-start gap-3">
                      <div className="flex items-center justify-center w-6 h-6 rounded-full bg-teal-100 dark:bg-teal-900 text-xs font-medium text-teal-700 dark:text-teal-300">
                        {index + 1}
                      </div>
                      <div className="flex-1 space-y-2">
                        <div className="grid grid-cols-3 gap-2">
                          <Input
                            value={field.name}
                            onChange={(e) => updateField(field.id, { name: e.target.value })}
                            placeholder="Field name"
                            className="text-sm"
                          />
                          <Select
                            value={field.type}
                            onValueChange={(v) => updateField(field.id, { type: v as any })}
                          >
                            <SelectTrigger className="text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {FIELD_TYPES.map(t => (
                                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={field.required}
                              onCheckedChange={(checked) => updateField(field.id, { required: !!checked })}
                            />
                            <span className="text-xs">Required</span>
                          </div>
                        </div>
                        <Input
                          value={field.question}
                          onChange={(e) => updateField(field.id, { question: e.target.value })}
                          placeholder="Question to ask"
                          className="text-sm"
                        />
                        <div className="flex items-center justify-between">
                          <code className="text-xs bg-muted px-2 py-0.5 rounded">
                            {field.variableName}
                          </code>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeField(field.id)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Actions */}
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <ArrowRight className="h-4 w-4 text-teal-500" />
              Actions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between p-2 rounded-lg border bg-muted/30">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-blue-500" />
                <span className="text-sm">Book Appointment</span>
              </div>
              <Checkbox
                checked={requirements.actions.bookAppointment}
                onCheckedChange={(checked) => setRequirements({
                  ...requirements,
                  actions: { ...requirements.actions, bookAppointment: !!checked }
                })}
              />
            </div>

            {requirements.actions.bookAppointment && requirements.dataReuseInfo.appointmentWillReuseData && (
              <Alert className="bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800">
                <RefreshCw className="h-4 w-4 text-emerald-600" />
                <AlertDescription className="text-emerald-800 dark:text-emerald-200 text-sm">
                  <strong>Smart Data Reuse:</strong> Appointment will use already-collected
                  {requirements.dataReuseInfo.hasNameField && " name"}
                  {requirements.dataReuseInfo.hasEmailField && " email"}
                  {requirements.dataReuseInfo.hasPhoneField && " phone"}
                  . Only date, time & meeting preference will be asked!
                </AlertDescription>
              </Alert>
            )}

            <div className="flex items-center justify-between p-2 rounded-lg border bg-muted/30">
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-orange-500" />
                <span className="text-sm">Transfer to Agent</span>
              </div>
              <Checkbox
                checked={requirements.actions.transferToAgent}
                onCheckedChange={(checked) => setRequirements({
                  ...requirements,
                  actions: { ...requirements.actions, transferToAgent: !!checked }
                })}
              />
            </div>
          </CardContent>
        </Card>

        {/* Suggestions */}
        {requirements.suggestions.length > 0 && (
          <Alert>
            <Lightbulb className="h-4 w-4" />
            <AlertDescription>
              <p className="font-medium mb-1 text-sm">Suggestions:</p>
              <ul className="list-disc list-inside text-sm space-y-1">
                {requirements.suggestions.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={() => setStep('upload')}>
            <ChevronLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={generateMutation.isPending}
            className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700"
          >
            {generateMutation.isPending ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating...</>
            ) : (
              <><Sparkles className="h-4 w-4 mr-2" /> Generate Flow</>
            )}
          </Button>
        </div>
      </div>
    );
  };

  // Render Generate Step
  const renderGenerateStep = () => {
    // State Machine Result UI
    if (stateMachineResult) {
      return (
        <div className="max-w-2xl mx-auto space-y-6">
          <div className="flex items-center gap-2 text-purple-600">
            <Workflow className="h-6 w-6" />
            <span className="font-medium text-lg">Smart Follow-Up Workflow Created!</span>
          </div>

          <Alert className="bg-gradient-to-r from-purple-50 to-violet-50 dark:from-purple-950/30 dark:to-violet-950/30 border-purple-200 dark:border-purple-800">
            <Sparkles className="h-4 w-4 text-purple-600" />
            <AlertDescription className="text-purple-800 dark:text-purple-200">
              <p className="font-medium mb-2">Multi-Call State Machine Created!</p>
              <p className="text-sm">
                This workflow will automatically orchestrate multiple calls with smart follow-ups.
                Create a campaign to start using it.
              </p>
            </AlertDescription>
          </Alert>

          <Card>
            <CardContent className="pt-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Campaign Name</Label>
                  <Input
                    value={campaignName}
                    onChange={(e) => setCampaignName(e.target.value)}
                    placeholder={stateMachineResult.stateMachine.name}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">AI Agent <span className="text-red-500">*</span></Label>
                  <Select value={selectedAgentId} onValueChange={setSelectedAgentId}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select an agent" />
                    </SelectTrigger>
                    <SelectContent>
                      {agents.length === 0 ? (
                        <SelectItem value="_none" disabled>No agents available</SelectItem>
                      ) : (
                        agents.map((agent) => (
                          <SelectItem key={agent.id} value={agent.id}>
                            {agent.name} {agent.type === 'flow' && '(Flow)'}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  {agents.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">
                      Create an agent first to use with this campaign
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 text-sm">
                <div className="text-center p-3 rounded-lg bg-purple-50 dark:bg-purple-950/50">
                  <div className="text-2xl font-bold text-purple-600">{stateMachineResult.blueprint.statesCount || 0}</div>
                  <div className="text-xs text-muted-foreground">States</div>
                </div>
                <div className="text-center p-3 rounded-lg bg-violet-50 dark:bg-violet-950/50">
                  <div className="text-2xl font-bold text-violet-600">{stateMachineResult.flows.length}</div>
                  <div className="text-xs text-muted-foreground">Flows</div>
                </div>
                <div className="text-center p-3 rounded-lg bg-fuchsia-50 dark:bg-fuchsia-950/50">
                  <div className="text-2xl font-bold text-fuchsia-600">{stateMachineResult.blueprint.transitionsCount || 0}</div>
                  <div className="text-xs text-muted-foreground">Transitions</div>
                </div>
              </div>

              {stateMachineResult.flows.length > 0 && (
                <div>
                  <Label className="text-xs text-muted-foreground mb-2 block">Generated Flows</Label>
                  <div className="flex flex-wrap gap-1">
                    {stateMachineResult.flows.map((flow, i) => (
                      <Badge key={i} variant="outline" className="text-xs">
                        {flow.stateName}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {stateMachineResult.blueprint.persistedVariables && stateMachineResult.blueprint.persistedVariables.length > 0 && (
                <div>
                  <Label className="text-xs text-muted-foreground mb-2 block">Cross-Call Variables</Label>
                  <div className="flex flex-wrap gap-1">
                    {stateMachineResult.blueprint.persistedVariables.map((v, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">
                        {`{{${v}}}`}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    These variables persist across calls for personalized follow-ups.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex justify-between pt-4">
            <Button variant="outline" onClick={() => setStep('review')}>
              <ChevronLeft className="h-4 w-4 mr-1" /> Edit Requirements
            </Button>
            <Button
              onClick={handleCreateCampaign}
              disabled={createCampaignMutation.isPending || !selectedAgentId}
              className="bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700"
            >
              {createCampaignMutation.isPending ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating Campaign...</>
              ) : (
                <><Rocket className="h-4 w-4 mr-2" /> Create Campaign</>
              )}
            </Button>
          </div>
        </div>
      );
    }

    // Single Flow Result UI
    if (!generatedFlow) return null;

    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center gap-2 text-green-600">
          <CheckCircle className="h-6 w-6" />
          <span className="font-medium text-lg">Flow Generated Successfully!</span>
        </div>

        <Card>
          <CardContent className="pt-6 space-y-4">
            <div>
              <Label className="text-xs text-muted-foreground">Flow Name</Label>
              <Input
                value={flowName || generatedFlow.name}
                onChange={(e) => setFlowName(e.target.value)}
                className="mt-1"
              />
            </div>

            <div className="flex gap-6 text-sm">
              <div>
                <span className="text-muted-foreground">Nodes:</span>{" "}
                <Badge variant="secondary" className="ml-1">{generatedFlow.nodes?.length || 0}</Badge>
              </div>
              <div>
                <span className="text-muted-foreground">Connections:</span>{" "}
                <Badge variant="secondary" className="ml-1">{generatedFlow.edges?.length || 0}</Badge>
              </div>
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">Node Types</Label>
              <div className="flex flex-wrap gap-1 mt-2">
                {generatedFlow.nodes?.map((node: any, index: number) => (
                  <Badge key={index} variant="outline" className="text-xs">
                    {node.data?.label || node.type}
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Form Created */}
        {createdForm && (
          <Alert className="bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800">
            <ClipboardList className="h-4 w-4 text-emerald-600" />
            <AlertDescription className="text-emerald-800 dark:text-emerald-200">
              <p className="font-medium">Data Collection Form Created!</p>
              <p className="text-sm">
                Form "{createdForm.name}" with {createdForm.fields?.length || 0} fields
              </p>
            </AlertDescription>
          </Alert>
        )}

        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={() => setStep('review')}>
            <ChevronLeft className="h-4 w-4 mr-1" /> Edit Requirements
          </Button>
          <Button
            onClick={handleSave}
            disabled={updateAIFlowMutation.isPending}
            className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700"
          >
            {updateAIFlowMutation.isPending ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
            ) : (
              <><CheckCircle className="h-4 w-4 mr-2" /> Save Flow</>
            )}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={handleCancel}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Flows
              </Button>
              <Separator orientation="vertical" className="h-6" />
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
                  <Sparkles className="h-4 w-4 text-white" />
                </div>
                <div>
                  <h1 className="font-semibold">AI Flow Creator</h1>
                  <p className="text-xs text-muted-foreground">
                    {step === 'template' && "Select a template"}
                    {step === 'upload' && "Upload your document"}
                    {step === 'review' && "Review requirements"}
                    {step === 'generate' && "Save your flow"}
                  </p>
                </div>
              </div>
            </div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="icon">
                    <HelpCircle className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="left" className="max-w-sm p-4">
                  <p className="font-medium mb-2">How This Works</p>
                  <ol className="text-sm space-y-1 list-decimal list-inside">
                    <li>Select a template that matches your use case</li>
                    <li>Upload a document describing your flow requirements</li>
                    <li>Review and edit the extracted information</li>
                    <li>Generate and save your AI flow</li>
                  </ol>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        {renderStepIndicator()}

        <div className="mt-8">
          {step === 'template' && renderTemplateStep()}
          {step === 'upload' && renderUploadStep()}
          {step === 'review' && renderReviewStep()}
          {step === 'generate' && renderGenerateStep()}
        </div>
      </div>
    </div>
  );
}
